import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  Grid,
  Stack,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  FormHelperText,
  DialogTitle,
  IconButton,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { dukaElements } from '../../../api/Duka/api';
import { getStocks } from '../../../api/Stock Management/api';
import { X } from 'lucide-react';
import StockSelector from "../../../pages/StockManagement/components/StockSelector";

const ORDER_TYPES = {
  CASH: 'CASH',
  CREDIT: 'CREDIT',
};

const ORDER_STATUS = {
  PENDING: 'PENDING',
  PROCESSING: 'PROCESSING',
  SHIPPED: 'SHIPPED',
  DELIVERED: 'DELIVERED',
  CANCELLED: 'CANCELLED'
};

const OrderForm = ({ initialData, onSubmit, onCancel }) => {
  const [dukas, setDukas] = useState([]);
  const [products, setProducts] = useState([]);
  const [existingStocks, setExistingStocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [selectedStockId, setSelectedStockId] = useState(initialData?.selected_stock || null);
  
  const [formData, setFormData] = useState({
    dukaId: '',
    productId: initialData?.product || "",
    orderType: initialData?.order_type || '',
    quantity: initialData?.quantity || '',
    unitPrice: initialData?.unit_price || '',
    orderDate: initialData?.order_date ? dayjs(initialData.order_date) : dayjs(),
    deliveryDate: initialData?.delivery_date ? dayjs(initialData.delivery_date) : dayjs(),
    status: initialData?.status || ORDER_STATUS.PENDING
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [dukaResponse, productResponse] = await Promise.all([
          dukaElements("dukas"),
          dukaElements('products')
        ]);
        setDukas(dukaResponse.data);
        setProducts(productResponse.data);

        if (initialData) {
          const matchingDuka = dukaResponse.data.find(
            d => d.name.toLowerCase() === initialData.duka.toLowerCase()
          );
          const matchingProduct = productResponse.data.find(
            p => p.id === initialData.product
          );
          setFormData(prev => ({
            ...prev,
            dukaId: matchingDuka?.id || '',
            productId: matchingProduct?.id || ''
          }));
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [initialData]);

  useEffect(() => {
    if (formData.productId) {
      const fetchStocks = async () => {
        try {
          const response = await getStocks({ product: formData.productId });
          if (response && response.data) {
            setExistingStocks(response.data);
          } else {
            setExistingStocks([]);
          }
        } catch (error) {
          console.error("Error fetching stocks:", error);
          setExistingStocks([]);
        }
      };
      fetchStocks();
    } else {
      setExistingStocks([]);
    }
  }, [formData.productId]);
  
  useEffect(() => {
    if (initialData) {
      setSelectedStockId(initialData.selected_stock || null);
    }
  }, [initialData]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.dukaId) {
      newErrors.dukaId = 'Duka is required';
    }
    if (!formData.productId) {
      newErrors.productId = 'Product is required';
    }
    if (!formData.orderType) {
      newErrors.orderType = 'Order type is required';
    }
    if (!formData.quantity || formData.quantity < 1) {
      newErrors.quantity = 'Quantity must be at least 1';
    }
    if (!formData.status) {
      newErrors.status = 'Status is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'productId' && {
        unitPrice: products.find(p => p.id === value)?.price || 0
      })
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleDateChange = (field, newValue) => {
    setFormData(prev => ({
      ...prev,
      [field]: newValue
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Find the selected duka and product objects
    const selectedDuka = dukas.find(d => d.id === formData.dukaId);
    const selectedProduct = products.find(p => p.id === formData.productId);
    
    if (!selectedDuka || !selectedProduct) {
      setErrors(prev => ({
        ...prev,
        dukaId: !selectedDuka ? 'Invalid duka selected' : undefined,
        productId: !selectedProduct ? 'Invalid product selected' : undefined
      }));
      return;
    }

    const transformedData = {
      duka: selectedDuka.id,
      product: selectedProduct.id,
      order_type: formData.orderType,
      status: formData.status,
      quantity: parseInt(formData.quantity),
      unit_price: formData.unitPrice ? parseFloat(formData.unitPrice).toFixed(2) : "0.00",
      order_date: formData.orderDate.toISOString(),
      delivery_date: formData.deliveryDate.toISOString(),
      selected_stock: selectedStockId
    };

    onSubmit(transformedData);
  };

  return (
    <Box sx={{ p: 2 }}>
      <DialogTitle
        sx={{
          p: 1,
          fontSize: 18,
          fontWeight: 500,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        {initialData ? 'Edit Order' : 'Add Order'}
        <IconButton 
          onClick={onCancel}
          size="small"
          sx={{ color: 'text.secondary' }}
        >
          <X size={20} />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormControl fullWidth error={!!errors.dukaId}>
              <InputLabel id="duka-label">Duka</InputLabel>
              <Select
                labelId="duka-label"
                name="dukaId"
                value={formData.dukaId}
                onChange={handleChange}
                label="Duka"
                required
                disabled={loading}
              >
                {dukas.map((duka) => (
                  <MenuItem key={duka.id} value={duka.id}>
                  {duka.code} | {duka.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.dukaId && <FormHelperText>{errors.dukaId}</FormHelperText>}
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth error={!!errors.productId}>
              <InputLabel id="product-label">Product</InputLabel>
              <Select
                labelId="product-label"
                name="productId"
                value={formData.productId}
                onChange={handleChange}
                label="Product"
                required
                disabled={loading}
              >
                {products.map((product) => (
                  <MenuItem key={product.id} value={product.id}>
                  {product.code} | {product.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.productId && <FormHelperText>{errors.productId}</FormHelperText>}
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <StockSelector
              product={products.find(p => p.id === formData.productId)?.name || ""}
              productId={formData.productId}
              value={selectedStockId}
              defaultStockId={selectedStockId}
              onChange={(e) => {
                const stockId = e.target.value;
                setSelectedStockId(stockId);
                const selectedStock = existingStocks.find(s => s.id === stockId);
                if (selectedStock) {
                  setFormData(prev => ({
                    ...prev,
                    serving_warehouse: selectedStock.serving_warehouse,
                    stock_region: selectedStock.stock_region,
                    available_quantity: selectedStock.available_quantity,
                    reserved_quantity: selectedStock.reserved_quantity,
                  }));
                }
              }}
              onSelectStock={(selectedStock) => {
                setFormData(prev => ({
                  ...prev,
                  serving_warehouse: selectedStock.serving_warehouse,
                  stock_region: selectedStock.stock_region,
                  available_quantity: selectedStock.available_quantity,
                  reserved_quantity: selectedStock.reserved_quantity,
                }));
              }}
              error={errors.stock}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth error={!!errors.orderType}>
              <InputLabel id="order-type-label">Order Type</InputLabel>
              <Select
                labelId="order-type-label"
                name="orderType"
                value={formData.orderType}
                onChange={handleChange}
                label="Order Type"
                required
              >
                {Object.entries(ORDER_TYPES).map(([key, value]) => (
                  <MenuItem key={key} value={value}>
                    {value}
                  </MenuItem>
                ))}
              </Select>
              {errors.orderType && <FormHelperText>{errors.orderType}</FormHelperText>}
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Quantity"
              name="quantity"
              type="number"
              value={formData.quantity}
              onChange={handleChange}
              required
              error={!!errors.quantity}
              helperText={errors.quantity}
              InputProps={{ inputProps: { min: 1 } }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Unit Price (KES)"
              name="unitPrice"
              type="number"
              disabled
              value={formData.unitPrice}
              onChange={handleChange}
              required
              error={!!errors.unitPrice}
              helperText={errors.unitPrice}
              InputProps={{
                inputProps: { min: 0, step: "0.01", readOnly: true }
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Order Date"
                value={formData.orderDate}
                onChange={(newValue) => handleDateChange('orderDate', newValue)}
                slotProps={{
                  textField: { fullWidth: true, required: true, error: !!errors.orderDate }
                }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Delivery Date"
                value={formData.deliveryDate}
                onChange={(newValue) => handleDateChange('deliveryDate', newValue)}
                slotProps={{
                  textField: { fullWidth: true, required: true, error: !!errors.deliveryDate }
                }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth error={!!errors.status}>
              <InputLabel id="status-label">Status</InputLabel>
              <Select
                labelId="status-label"
                name="status"
                value={formData.status}
                onChange={handleChange}
                label="Status"
                required
              >
                {Object.entries(ORDER_STATUS).map(([key, value]) => (
                  <MenuItem key={key} value={value}>
                    {value}
                  </MenuItem>
                ))}
              </Select>
              {errors.status && <FormHelperText>{errors.status}</FormHelperText>}
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="contained"
                onClick={onCancel}
                sx={{
                  bgcolor: '#e0e0e0',
                  color: '#000',
                  '&:hover': { bgcolor: '#d5d5d5' },
                  textTransform: 'none',
                  borderRadius: 2,
                  fontWeight: 600,
                  px: 3,
                  fontSize: '16px',
                  lineHeight: '24px'
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                type="submit"
                disabled={loading}
                sx={{
                  bgcolor: '#00694B',
                  color: '#fff',
                  '&:hover': { bgcolor: '#005039' },
                  textTransform: 'none',
                  borderRadius: 2,
                  fontWeight: 600,
                  px: 3,
                  fontSize: '16px',
                  lineHeight: '24px'
                }}
              >
                {initialData ? 'Update Order' : 'Add Order'}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default OrderForm;